/**
 * UploadToast — die **schwebende Toast-View** des Upload-Flows (2026-06-16).
 * Erscheint beim Minimieren des Upload-Modals (view==='toast'); der Upload läuft
 * im `UploadProvider` ununterbrochen weiter. Fixed bottom-right, zeigt Aggregat-
 * Progress + einen dünnen Gesamt-Balken.
 *
 * Zustände:
 *  - *uploading* — „X/Y hochgeladen" + Progress-Balken.
 *  - *fertig ohne Fehler* — „✓ N hochgeladen", auto-dismiss nach ~5 s (der Timer
 *    lebt HIER, R1-Punkt 3: Restore unmountet die Toast-View → Timer wird
 *    automatisch gecancelt; das verhindert die Race „Modal schließt sich 5 s nach
 *    Restore unterm User weg"). Auto-dismiss gilt NUR in der Toast-View.
 *  - *mit Fehlern* — „N fehlgeschlagen" persistiert (kein auto-dismiss); Restore
 *    öffnet das Modal mit den per-Datei „Erneut"-Buttons.
 *
 * a11y: Container `role="status"`/`aria-live="polite"` (Progress wird angesagt);
 * Restore + Dismiss sind explizite, beschriftete `<button>`s (kein Button-in-Button,
 * C8). Bei Mount springt der Focus auf den Restore-Button (C7) — Keyboard/SR
 * verlieren den aktiven Upload beim Minimieren nicht.
 */
import { useEffect, useRef } from 'react';
import { I } from '../lib/icons';
import { useUploadControl, useUploadEngine } from '../lib/upload-context';

const AUTO_DISMISS_MS = 5000;

export function UploadToast() {
  const { restore, requestClose } = useUploadControl();
  const { files, submitting } = useUploadEngine();
  const restoreRef = useRef<HTMLButtonElement | null>(null);

  const total = files.length;
  const doneCount = files.filter((f) => f.status === 'done').length;
  const errorCount = files.filter((f) => f.status === 'error').length;
  const finished = total > 0 && files.every((f) => f.status === 'done' || f.status === 'error');
  const hasErrors = errorCount > 0;
  // Aggregat-Progress: Mittelwert der per-Datei-%; done=100, pending=0.
  const aggregate =
    total === 0
      ? 0
      : Math.round(
          files.reduce((acc, f) => acc + (f.status === 'done' ? 100 : f.progress), 0) / total,
        );

  // C7: Focus beim Mount (= beim Minimieren) auf den Restore-Button.
  useEffect(() => {
    restoreRef.current?.focus();
  }, []);

  // R1-Punkt 3: Auto-dismiss-Timer lebt HIER. Nur „fertig ohne Fehler". Cleanup
  // on-unmount → Restore (view→'modal' unmountet die Toast-View) cancelt ihn.
  useEffect(() => {
    if (!finished || hasErrors) return;
    const id = window.setTimeout(() => requestClose(), AUTO_DISMISS_MS);
    return () => window.clearTimeout(id);
  }, [finished, hasErrors, requestClose]);

  const statusText = hasErrors
    ? `${errorCount} fehlgeschlagen, ${doneCount}/${total} hochgeladen`
    : finished
      ? `${doneCount} ${doneCount === 1 ? 'Datei' : 'Dateien'} hochgeladen`
      : submitting
        ? `${doneCount}/${total} hochgeladen`
        : `${total} ${total === 1 ? 'Datei' : 'Dateien'} bereit`;

  // Redesign S2 Phase 4a: Fortschritt grün (kein Gelb — Gelb gehört dem Topbar-CTA).
  const barClass = hasErrors ? 'bg-danger-600' : 'bg-brand-green-700';

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 w-[min(20rem,calc(100vw-2rem))] rounded-2xl border border-ink-200 bg-white p-4 shadow-pop"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-ink-900">
              {finished && !hasErrors ? '✓ Upload fertig' : 'Upload'}
            </span>
          </div>
          <p
            className={`mt-0.5 text-xs ${hasErrors ? 'text-danger-700' : 'text-ink-500'}`}
          >
            {statusText}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            ref={restoreRef}
            type="button"
            onClick={restore}
            aria-label="Upload anzeigen"
            className="rounded-control px-2.5 py-1 text-xs font-semibold text-brand-green-700 bg-brand-green-100 hover:bg-brand-green-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500 focus-visible:ring-offset-2"
          >
            Anzeigen
          </button>
          <button
            type="button"
            onClick={requestClose}
            aria-label="Upload-Hinweis schließen"
            className="rounded-control p-1 text-ink-500 hover:bg-ink-50 hover:text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500 focus-visible:ring-offset-2"
          >
            <I.close size={16} />
          </button>
        </div>
      </div>
      {/* Dünner Gesamt-Balken */}
      <div className="mt-3 h-1 overflow-hidden rounded-full bg-ink-100">
        <div
          className={`h-full transition-[width] duration-300 ${barClass}`}
          style={{ width: `${finished ? 100 : aggregate}%` }}
        />
      </div>
    </div>
  );
}
